import {
  Dialog as AriaDialog,
  Heading,
  Modal,
  ModalOverlay,
} from 'react-aria-components'
import type { ReactNode } from 'react'
import { cx } from '../../cx'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  actions?: ReactNode
  className?: string
  children?: ReactNode
  /** Blocks dismissal via Escape or backdrop click. Use for destructive confirms. */
  isDismissable?: boolean
}

/**
 * This is where the headless layer earns its keep. The design project's Dialog is
 * a styled div; a modal that is merely styled is unusable with a keyboard or a
 * screen reader.
 *
 * React Aria supplies: focus moved into the dialog on open and restored to the
 * trigger on close, focus trapped while open, Escape to dismiss, the rest of the
 * page marked aria-hidden, scroll locked on the body, and role="dialog" with
 * aria-labelledby bound to the heading.
 */
export function Dialog({
  open,
  onClose,
  title,
  actions,
  className,
  children,
  isDismissable = true,
}: DialogProps) {
  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
      isDismissable={isDismissable}
      className="bui-backdrop"
    >
      <Modal>
        <AriaDialog className={cx('bui-dialog', 'bui-glass', className)}>
          <Heading slot="title" className="bui-dialog-title">
            {title}
          </Heading>
          {children ? <div className="bui-dialog-body">{children}</div> : null}
          {actions ? <div className="bui-dialog-actions">{actions}</div> : null}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  )
}
